import React, {
  createContext,
  createRef,
  useImperativeHandle,
  forwardRef,
  useContext,
  useState,
  useMemo
} from 'react'
import { gql } from '@apollo/client'
import client from 'apollo-client'
import {
  removeAccount,
  convertAccountsToSites,
  checkInPerimeter
} from './utils'
import { cloneDeep } from 'lodash'
import { useUser, useSites, useRefreshListeners } from 'store'

const TreeContext = createContext(undefined)

export const useTreeContext = () => {
  const ctx = useContext(TreeContext)

  if (!ctx) {
    throw Error('useTreeContext must be use inside TreeContext')
  }

  return ctx
}

const OPERATION_ACCOUNTS_CHILDREN = gql`
  query GetAccounts($sf_id: String!) {
    accounts: accountsTree(sf_id: $sf_id) {
      id
      name
      hierarchie
      sf_parent_id  
      sf_id
      nb_children
      should_display_count
      is_site
      state
    }
  }
`

const fetchAccountsByNode = async node => {
  const { data, error } = await client.query({
    query: OPERATION_ACCOUNTS_CHILDREN,
    variables: {
      sf_id: node.data.data.sf_id
    }
  })
  if (error) {
    throw error
  }
  return data.accounts
}

export const useSitesSelected = ({ selection }) => {
  const sites = useMemo(() => {
    return convertAccountsToSites(selection)
      .map(({children, ...account}) => ({
        ...account,
        children: children.map(item => ({
          id: item.id,
          sf_id: item.sf_id,
          label: item.name ?? 'N/A',
          disabled: item?.disabled ?? false,
          state: item.state
        }))
      }))
  }, [selection])

  const count = useMemo(() => {
    return sites.reduce((acc, site) => {
      return acc + (site.children ? site.children.length : 0)
    }, 0)
  }, [sites])

  const first = useMemo(() => {
    return sites[0]?.children?.[0]
  }, [sites])
  
  return {
    firstSite: first,
    sites,
    count
  }
}

export const TreeProvider = forwardRef(({
  onlySite = false,
  canRemoveSelection = true,
  initialSelection = [],
  isMasterFilter = false,
  onSelectionReset = () => {},
  hideCounters = false,
  children
}, ref) => {
  const [user] = useUser()
  const [waiting, setWaiting] = useState(false)
  const [selection, setSelection] = useState(initialSelection)
  const [isFolded, setIsFolded] = useState(false)
  const [, setSites] = useSites()
  const [listeners] = useRefreshListeners()
  const treeRef = createRef(null)
  useImperativeHandle(ref, () => {
    return {
      reset: (newSelection) => {
        syncSelection(newSelection, {notify: false})
      }
    }
  }, [])

  const notifyRefresh = (newSelection) => {
    Object.values(listeners).forEach((callback) => {
      callback(newSelection)
    })
  }

  const syncSelection = (newSelection, {notify = true} = {}) => {
    setSelection(newSelection)
    if (isMasterFilter) {
      const newSites = newSelection.filter(({is_site}) => is_site)
      localStorage.setItem('sites', JSON.stringify({ selected: newSites, accounts: newSelection }))
      setSites(prev => ({...prev, selected: newSites, accounts: newSelection}))
      onSelectionReset(newSelection)
    }
    if (notify && isMasterFilter) {
      notifyRefresh(newSelection)
    }
  }

  const handleRemove = node => {
    let newAccounts = cloneDeep(selection)
    newAccounts = removeAccount({account: {sf_id: node.id}, accounts: newAccounts})
    newAccounts = checkInPerimeter({accounts: newAccounts})
    treeRef.current?.deselectAll()
    syncSelection(newAccounts)

    return newAccounts
  }

  const addMultiple = async node => {
    let newAccounts = cloneDeep(selection)
    newAccounts = removeAccount({ account: node.data.data, accounts: newAccounts })
    
    // si le compte est un site, il n'a pas de children
    // on doit donc trouver son parent pour respecter la hiérarchie de la selection
    if (node.data.data.is_site) {
      newAccounts = [...newAccounts, node.data.data]
      let parentAccount = newAccounts.find(({sf_id}) => sf_id === node.data.data.sf_parent_id)
      if (!parentAccount) {
        parentAccount = {...node.parent.data.data}
        newAccounts = [...newAccounts, parentAccount]
      }
      const nbChildren = newAccounts.filter(({hierarchie}) => {
        const [parentId] = hierarchie.slice(-2)
        return parentId === parentAccount.sf_id
      }).length
      parentAccount.in_perimeter = parentAccount.nb_children === nbChildren
    } else {
      const childrenAccounts = await fetchAccountsByNode(node)
      newAccounts = [...newAccounts, ...childrenAccounts]
    }
    newAccounts = checkInPerimeter({accounts: newAccounts})
    syncSelection(newAccounts)
  }

  const handleAdd = async node => {
    setWaiting(true)
    try {
      if (!onlySite) {
        await addMultiple(node)
      } else if (node.data.data.is_site) {
        let parentAccount = node.parent?.data?.data || {}
        const newAccounts = [{...parentAccount, in_perimeter: false}, {...node.data.data, in_perimeter: true}]
        syncSelection(newAccounts)
      }
    } finally {
      setWaiting(false)
    }
  }

  return (
    <TreeContext.Provider
      value={{
        treeRef,
        onlySite,
        accounts: user.accounts,
        selection,
        waiting,
        isFolded,
        hideCounters,
        canRemoveSelection,
        onFoldedToggle: () => {
          if (!treeRef.current) return;
          
          if (isFolded) {
            treeRef.current.openAll()
          } else {
            treeRef.current.closeAll()
          }
          setIsFolded(!isFolded)
        },        
        onNodeRemoveAll: () => {
          if (treeRef.current) {
            treeRef.current.deselectAll()
          }
          syncSelection([])
        },        
        onNodeAdd: async node => {
          await handleAdd(node)
        },
        onNodeRemove: node => {
          handleRemove(node)
        },
        resetSelection: async (newSelection) => {
          syncSelection(newSelection, {notify: false})
        },
        onWaitingStart: () => {
          setWaiting(true)
        },
        onWaitingEnd: () => {
          setWaiting(false)
        }
      }}>
      {children}
    </TreeContext.Provider>
  )
})

export default TreeProvider
